import { createContext, useContext, useEffect, useRef, useState, useCallback, type ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { PreferenceClientLike } from '@/utils/userPreferenceStore';
import {
  DEFAULT_PALETTE, applyPalette, clearPalette, isPersonalPaletteRoute, normalizePalette, readCachedPalette, writeCachedPalette,
  type ColorPaletteId,
} from '@/lib/colorPalettes';

type Theme = 'system' | 'light' | 'dark';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  resolvedTheme: 'light' | 'dark';
  palette: ColorPaletteId;
  /** Applies immediately and persists to the signed-in user's own preferences. */
  setPalette: (palette: ColorPaletteId) => Promise<boolean>;
  /** Route scope: personal palettes paint only signed-in app surfaces. */
  setPaletteScopeActive: (active: boolean) => void;
  paletteStatus: PaletteSaveStatus;
  retryPaletteSave: () => Promise<boolean>;
}

export type PaletteSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

type BindableClient = {
  auth: { getUser: () => Promise<{ data: { user: { id: string } | null } }> };
  from: (table: string) => unknown;
};

/**
 * Binds a preference read/write to the account that initiated it: if the
 * live session has moved to another account, the store sees "no user" and
 * refuses (and RLS still limits writes to the caller's own row).
 */
export function bindPreferenceClient(expectedUserId: string, base: BindableClient = supabase as unknown as BindableClient) {
  return {
    auth: {
      getUser: async () => {
        const res = await base.auth.getUser();
        return res.data.user?.id === expectedUserId ? res : { data: { user: null } };
      },
    },
    from: (table: string) => base.from(table),
  } as unknown as PreferenceClientLike;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'system',
  setTheme: () => {},
  resolvedTheme: 'light',
  palette: DEFAULT_PALETTE,
  setPalette: async () => false,
  setPaletteScopeActive: () => {},
  paletteStatus: 'idle',
  retryPaletteSave: async () => false,
});

export const useTheme = () => useContext(ThemeContext);

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(resolved: 'light' | 'dark') {
  const root = document.documentElement;
  if (resolved === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>('system');
  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>(getSystemTheme);
  const [palette, setPaletteState] = useState<ColorPaletteId>(DEFAULT_PALETTE);
  const userIdRef = useRef<string | null>(null);
  const [scopeActive, setPaletteScopeActive] = useState<boolean>(() =>
    typeof window !== 'undefined' && isPersonalPaletteRoute(window.location.pathname));

  const resolvedTheme = theme === 'system' ? systemTheme : theme;

  // Listen for OS theme changes
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemTheme(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Apply class to <html>
  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme]);

  useEffect(() => {
    // Public/non-app routes: remove the attribute so the original brand CSS
    // (`:root` / `.dark`) applies untouched.
    if (scopeActive) applyPalette(palette);
    else clearPalette();
  }, [palette, scopeActive]);

  // Palette persistence state. `savedRef` = last value confirmed persisted for
  // the current account; `desiredRef` = newest local choice.
  const savedRef = useRef<ColorPaletteId>(DEFAULT_PALETTE);
  const desiredRef = useRef<ColorPaletteId>(DEFAULT_PALETTE);
  const choiceSeqRef = useRef(0);
  // Theme choice tracking: an explicit local choice (dirty) wins over any
  // slower preference read or token-refresh reload until an authoritative
  // load applies or the account changes.
  const themeChoiceSeqRef = useRef(0);
  const themeDirtyRef = useRef(false);
  // Each save pump is owned by one account; a new account never waits on or is cleared by an old pump.
  const runRef = useRef<{ userId: string; promise: Promise<void> } | null>(null);
  const [paletteStatus, setPaletteStatus] = useState<PaletteSaveStatus>('idle');

  const resetForAccount = useCallback((userId: string | null) => {
    // Drop the previous account's pump ownership; its loop exits on its own.
    runRef.current = null;
    userIdRef.current = userId;
    const initial = userId ? (readCachedPalette(userId) ?? DEFAULT_PALETTE) : DEFAULT_PALETTE;
    savedRef.current = initial;
    desiredRef.current = initial;
    choiceSeqRef.current++;
    setPaletteStatus('idle');
    setPaletteState(initial);
    // One account's theme choice never paints another account.
    themeChoiceSeqRef.current++;
    themeDirtyRef.current = false;
    setThemeState('system');
  }, []);

  // Load persisted theme + palette from DB (signed-in users only — the
  // `anon` role has no SELECT grant on user_preferences). Account switches
  // reset the palette so one account's choice never paints another's.
  useEffect(() => {
    let cancelled = false;
    let generation = 0;

    const load = async (userId: string | null) => {
      const gen = ++generation;
      if (userIdRef.current !== userId) resetForAccount(userId);
      if (!userId) return;
      const seqAtStart = choiceSeqRef.current;
      const themeSeqAtStart = themeChoiceSeqRef.current;

      const { loadUserPreferences } = await import('@/utils/userPreferenceStore');
      const loadedPrefs = await loadUserPreferences(bindPreferenceClient(userId));
      if (cancelled || gen !== generation || loadedPrefs.userId !== userId || userIdRef.current !== userId) return;

      const site = loadedPrefs.siteSettings as Record<string, unknown>;
      // A newer explicit local choice wins over a slower load or a
      // token-refresh reload; the stored value only applies when the user
      // has not chosen anything since the last authoritative load.
      if (
        themeChoiceSeqRef.current === themeSeqAtStart &&
        !themeDirtyRef.current &&
        typeof site.theme === 'string' &&
        ['system', 'light', 'dark'].includes(site.theme)
      ) {
        setThemeState(site.theme as Theme);
      }
      // A newer local choice (or an in-flight/failed save) wins over a slower
      // load or a token-refresh reload.
      if (choiceSeqRef.current !== seqAtStart || runRef.current?.userId === userId || desiredRef.current !== savedRef.current) return;
      // Only personal rows carry a palette choice; the legacy shared seed never does.
      const next = normalizePalette(loadedPrefs.fromLegacyFallback ? undefined : site.palette);
      savedRef.current = next;
      desiredRef.current = next;
      setPaletteState(next);
      writeCachedPalette(userId, next);
    };

    void supabase.auth.getSession().then(({ data: { session } }) => {
      if (!cancelled) void load(session?.user?.id ?? null);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      void load(session?.user?.id ?? null);
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [resetForAccount]);


  const setTheme = useCallback((newTheme: Theme) => {
    themeChoiceSeqRef.current++;
    themeDirtyRef.current = true;
    setThemeState(newTheme);
    // Persist is handled by the Settings dialog save — this just updates runtime
  }, []);

  /** Serialized, coalescing save loop: only the newest choice is written, one at a time. */
  const runSaves = useCallback((): Promise<void> => {
    const userId = userIdRef.current;
    if (!userId) return Promise.resolve();
    if (runRef.current?.userId === userId) return runRef.current.promise;
    // Register ownership BEFORE starting, so a no-op pump that settles
    // immediately can never leave a stale entry behind.
    const entry: { userId: string; promise: Promise<void> } = { userId, promise: Promise.resolve() };
    runRef.current = entry;
    entry.promise = (async () => {
      try {
        await Promise.resolve();
        while (userIdRef.current === userId && desiredRef.current !== savedRef.current) {
          const target = desiredRef.current;
          setPaletteStatus('saving');
          let ok = false;
          try {
            const { saveUserPreferences } = await import('@/utils/userPreferenceStore');
            ok = (await saveUserPreferences({ site: { palette: target } }, bindPreferenceClient(userId))).ok;
          } catch {
            ok = false;
          }
          if (userIdRef.current !== userId) return;
          if (!ok) { setPaletteStatus('error'); return; }
          savedRef.current = target;
          writeCachedPalette(userId, target); // cache only confirmed values
        }
        if (userIdRef.current === userId) setPaletteStatus('saved');
      } finally {
        if (runRef.current === entry) runRef.current = null;
      }
    })();
    return entry.promise;
  }, []);

  const setPalette = useCallback(async (next: ColorPaletteId) => {
    const userId = userIdRef.current;
    choiceSeqRef.current++;
    desiredRef.current = next;
    setPaletteState(next);
    if (!userId) { setPaletteStatus('error'); return false; }
    // Joins the in-flight loop if any; the loop re-checks and writes the newest choice.
    await runSaves();
    // True when the account's newest choice is persisted (a superseded call is not a failure).
    return userIdRef.current === userId && savedRef.current === desiredRef.current;
  }, [runSaves]);

  const retryPaletteSave = useCallback(async () => {
    const userId = userIdRef.current;
    await runSaves();
    return userIdRef.current === userId && savedRef.current === desiredRef.current;
  }, [runSaves]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, resolvedTheme, palette, setPalette, setPaletteScopeActive, paletteStatus, retryPaletteSave }}>
      {children}
    </ThemeContext.Provider>
  );
}

/** Mount inside the router; toggles personal palette by route. */
export function PaletteRouteScope({ pathname }: { pathname: string }) {
  const { setPaletteScopeActive } = useTheme();
  useEffect(() => { setPaletteScopeActive(isPersonalPaletteRoute(pathname)); }, [pathname, setPaletteScopeActive]);
  return null;
}
